import type { LabsStore } from '@/app/(platform)/(subscription-gated)/labs/_engine/store';
import type { VerticalPack } from '@/app/(platform)/(subscription-gated)/labs/_engine/types';

/**
 * The takeaway. What a reader owns after they close the tab.
 *
 * 🔴 WHY THIS EXISTS (Adam, 2026-08-28). Every lab used to end on a screen that evaporated. Adam's
 * question was "what is the most basic version of something where they'd say, oh wow, I just did that",
 * and his point was that a label or a badge is not that. Something they could take into an interview
 * is. He also named the second-order effect: an artifact is the best hook we have for an email, because
 * the ask stops being a toll on the result and becomes a delivery address for their own work.
 *
 * 🔴 THE ON-SCREEN RESULT STAYS FREE. THE TAKEAWAY IS WHAT THE EMAIL BUYS. (Jay, 2026-08-28.) A cold
 * reader who has never heard of Extern still sees their archetype, their read and their matched
 * externships without giving us anything, because on this surface the explanation IS the pitch. What
 * the email unlocks is a NEW thing we are adding, not something we took away and sold back. Anyone
 * changing this file should keep that line exactly where it is.
 *
 * TWO PAGES, TWO JOBS (Jay, 2026-08-28, "both in one file"):
 *
 *   Page 1 — THE READOUT. About the reader. Their archetype as an identity in poster type, their
 *   mindset fingerprint (the four slider positions, plotted), which half of the work pulled them, and
 *   their own one-line read of the case. Dark ground, themed by the archetype's own accent, so the
 *   file visibly belongs to the lab they just did. This is the page they keep because it is about them.
 *
 *   Page 2 — THE WORK SAMPLE. About the work. The case, the rates they computed against the actuals,
 *   what the numbers showed, how they work. Light, restrained, memo-like. This is the page they can
 *   put in front of an interviewer, and it is honest about being a practice case.
 *
 * EVERYTHING HERE IS SOMETHING THEY ACTUALLY DID. No filler, no invented praise, no sample text passed
 * off as theirs. A section with nothing real behind it is omitted rather than padded, which is why
 * every field below is allowed to be empty.
 */

/** One rate the reader computed, with what they said and what was true. */
export interface ArtifactRate {
  correct: number;
  /** True when their pick was within the pack's grading tolerance. */
  gotIt: boolean;
  name: string;
  period: string;
  /** What they picked, or null if they never answered this one. */
  theirAnswer: number | null;
  unit: string;
}

/**
 * One mindset slider they answered: where their dot sits between two named instincts.
 *
 * This is the most personal data the lab collects, and the old artifact threw it away. Four dots on
 * four labelled lines is a fingerprint no other reader shares, which is precisely what makes the file
 * feel owned rather than issued.
 */
export interface ArtifactFingerprintRow {
  leftLabel: string;
  rightLabel: string;
  /** Slider position, 0 (fully left) to {@link FINGERPRINT_MAX} (fully right). */
  value: number;
}

/** Which half of the day-in-the-life work pulled them, counted from their right-swipes. */
export interface ArtifactLean {
  aAccent: string;
  aCount: number;
  aLabel: string;
  bAccent: string;
  bCount: number;
  bLabel: string;
}

export interface PreviewArtifact {
  /** The archetype's own accent. Themes the whole readout, exactly as it themes the lab. */
  accent: string;
  archetypeBody: string;
  archetypeName: string;
  archetypeTagline: string;
  /** The reader's own words. Empty when they typed nothing; the section is then omitted. */
  diagnosis: string;
  /** What the case actually showed, from the pack. Clearly labelled as the case note, not their words. */
  finding: string;
  /** The dots. Empty when they answered no sliders; the block is then omitted. */
  fingerprint: readonly ArtifactFingerprintRow[];
  /** The day-in-the-life activities they said were them. */
  interests: readonly string[];
  /** Null when they right-swiped nothing, in which case the block is omitted rather than faked 50/50. */
  lean: ArtifactLean | null;
  rates: readonly ArtifactRate[];
  /** How many rates they got right, over how many they answered. */
  score: { answered: number; right: number };
  subjectName: string;
  trackName: string;
}

/** The slider scale's top end. Matches the engine's 0-6 sliders. */
const FINGERPRINT_MAX = 6;

type ArtifactState = Pick<LabsStore, 'archetype' | 'm1Answers' | 'm2Answers' | 'm3Answers' | 'm3Diagnosis'>;

/**
 * Build the takeaway from the pack and the run.
 *
 * The archetype is re-derived when the store has not resolved one, exactly as the payoff screen does.
 * Without that a reader on a restored draft gets an artifact with a blank name on it, which is worse
 * than no artifact.
 */
export function buildPreviewArtifact(pack: VerticalPack, state: ArtifactState): PreviewArtifact {
  const archetypeId = state.archetype ?? pack.deriveArchetype(state.m2Answers);
  const archetype = pack.archetypes[archetypeId];

  const rates: ArtifactRate[] = pack.m3.metrics.map((metric) => {
    const raw = state.m3Answers[metric.key];
    const theirAnswer = raw === undefined ? null : Number(raw);
    const answered = theirAnswer !== null && Number.isFinite(theirAnswer);

    return {
      correct: metric.correct,
      gotIt: answered && Math.abs(theirAnswer - metric.correct) <= pack.m3.tolerance,
      name: metric.name,
      period: metric.period,
      theirAnswer: answered ? theirAnswer : null,
      unit: pack.m3.unitSuffix,
    };
  });

  const answeredRates = rates.filter((rate) => rate.theirAnswer !== null);

  const fingerprint: ArtifactFingerprintRow[] = pack.m2.questions.flatMap((question) => {
    const value = state.m2Answers[question.id];
    if (value === undefined || !Number.isFinite(value)) {
      return [];
    }
    return [
      {
        leftLabel: question.leftLabel,
        rightLabel: question.rightLabel,
        value: Math.min(Math.max(value, 0), FINGERPRINT_MAX),
      },
    ];
  });

  // Counted from RIGHT swipes only: a right-swipe is "that's me", which is the only signal that says
  // anything about pull. Rejecting a delivery card is not evidence of a diagnosis lean.
  const chosen = pack.m1.cards.filter((card) => state.m1Answers[card.id] === 'right');
  const aCount = chosen.filter((card) => card.pole === 'a').length;
  const bCount = chosen.length - aCount;
  const lean: ArtifactLean | null =
    chosen.length === 0
      ? null
      : {
          aAccent: pack.axis.poles.a.accent,
          aCount,
          aLabel: pack.axis.poles.a.label,
          bAccent: pack.axis.poles.b.accent,
          bCount,
          bLabel: pack.axis.poles.b.label,
        };

  return {
    accent: archetype?.accent ?? pack.axis.bothAccent,
    archetypeBody: archetype?.body ?? '',
    archetypeName: archetype?.displayName ?? '',
    archetypeTagline: archetype?.tagline ?? '',
    // Trimmed, because a textarea of whitespace is not a diagnosis.
    diagnosis: state.m3Diagnosis.trim(),
    finding: pack.copy.m3.activity.revealBody,
    fingerprint,
    interests: chosen.map((card) => card.text),
    lean,
    rates,
    score: { answered: answeredRates.length, right: answeredRates.filter((rate) => rate.gotIt).length },
    subjectName: pack.m3.subject.name,
    trackName: pack.displayName,
  };
}

/** Escape text destined for the downloaded HTML. The reader's own diagnosis goes through here. */
function escapeHtml(value: string): string {
  return value.replace(/&/g, '&amp;').replace(/</g, '&lt;').replace(/>/g, '&gt;').replace(/"/g, '&quot;');
}

const formatRate = (value: number, unit: string): string => `${value}${unit}`;

/** The readout's mindset fingerprint: four dots on four labelled tracks. */
function fingerprintHtml(rows: readonly ArtifactFingerprintRow[]): string {
  if (rows.length === 0) {
    return '';
  }

  const lines = rows
    .map((row) => {
      const percent = Math.round((row.value / FINGERPRINT_MAX) * 100);
      return `<div class="fp-row"><span class="fp-label">${escapeHtml(row.leftLabel)}</span><span class="fp-track"><span class="fp-dot" style="left:${percent}%"></span></span><span class="fp-label fp-right">${escapeHtml(row.rightLabel)}</span></div>`;
    })
    .join('');

  return `<div class="panel"><h3>How I think, plotted</h3>${lines}</div>`;
}

/** The readout's pull bar: which half of the work they said "that's me" to. */
function leanHtml(lean: ArtifactLean | null): string {
  if (!lean) {
    return '';
  }

  const total = lean.aCount + lean.bCount;
  const aShare = Math.round((lean.aCount / total) * 100);

  return `<div class="panel"><h3>The work that pulled me</h3>
<div class="lean-bar"><span style="width:${aShare}%;background:${escapeHtml(lean.aAccent)}"></span><span style="width:${100 - aShare}%;background:${escapeHtml(lean.bAccent)}"></span></div>
<div class="lean-labels"><span><i style="background:${escapeHtml(lean.aAccent)}"></i>${escapeHtml(lean.aLabel)} · ${lean.aCount}</span><span><i style="background:${escapeHtml(lean.bAccent)}"></i>${escapeHtml(lean.bLabel)} · ${lean.bCount}</span></div>
</div>`;
}

/**
 * Render the artifact as a standalone HTML file the reader can keep.
 *
 * HTML rather than a PDF deliberately. It needs no library, no server and no fonts; it opens in any
 * browser, prints to PDF in one keystroke, and stays readable if someone opens it in five years. The
 * whole point is that this outlives the tab.
 *
 * `completedOn` is passed in rather than read from the clock so this stays pure and the same run
 * always renders the same file.
 */
export function renderArtifactHtml(artifact: PreviewArtifact, completedOn: string): string {
  const accent = escapeHtml(artifact.accent);

  const scorePanel =
    artifact.score.answered > 0
      ? `<div class="panel"><h3>Proved on a case</h3><p class="score"><strong>${artifact.score.right} of ${artifact.score.answered}</strong> ${artifact.score.answered === 1 ? 'rate' : 'rates'} computed correctly on ${escapeHtml(artifact.subjectName)}'s operating table.</p></div>`
      : '';

  const quote = artifact.diagnosis
    ? `<blockquote class="cover-quote">&ldquo;${escapeHtml(artifact.diagnosis)}&rdquo;<cite>my read of the case, in my own words</cite></blockquote>`
    : '';

  const rateRows = artifact.rates
    .map((rate) => {
      // Bound to a local so TypeScript narrows it; a boolean flag does not narrow the property.
      const { theirAnswer } = rate;
      const theirs =
        theirAnswer === null
          ? '<span class="skip">not answered</span>'
          : escapeHtml(formatRate(theirAnswer, rate.unit));
      const verdictFor = (): string => {
        if (theirAnswer === null) {
          return '';
        }
        return rate.gotIt ? '<span class="ok">correct</span>' : '<span class="off">off</span>';
      };
      return `<tr><th scope="row">${escapeHtml(rate.name)}<small>${escapeHtml(rate.period)}</small></th><td class="num">${theirs}</td><td class="num muted">${escapeHtml(formatRate(rate.correct, rate.unit))}</td><td>${verdictFor()}</td></tr>`;
    })
    .join('');

  const coverage = artifact.rates.map((rate) => rate.name.toLowerCase()).join(', ');

  const readBlock = artifact.diagnosis
    ? `<section><h2>My read</h2><blockquote>${escapeHtml(artifact.diagnosis)}</blockquote></section>`
    : '';

  const interestsBlock = artifact.interests.length
    ? `<section><h2>The work I want more of</h2><ul>${artifact.interests.map((text) => `<li>${escapeHtml(text)}</li>`).join('')}</ul></section>`
    : '';

  return `<!doctype html>
<html lang="en"><head><meta charset="utf-8"><meta name="viewport" content="width=device-width,initial-scale=1">
<title>${escapeHtml(artifact.archetypeName)} · Extern Labs readout</title>
<style>
  :root{--accent:${accent};--ink:#111827;--body:#374151;--muted:#6b7280;--line:#e5e7eb;
        --night:#0b0e1a;--night-line:rgba(255,255,255,.14);--night-muted:rgba(255,255,255,.62)}
  *{box-sizing:border-box;margin:0}
  /* overscroll-behavior: a swipe past either end stops here instead of chaining to whatever
     scrolls behind this document. In the payoff's iframe that chain is the QA-521 trap on mobile
     Safari; in the downloaded file it is harmless. */
  body{background:var(--night);color:var(--body);overscroll-behavior-y:none;
       font:16px/1.6 -apple-system,BlinkMacSystemFont,"Segoe UI",Roboto,Helvetica,Arial,sans-serif}

  /* ── Page 1 · the readout ─────────────────────────────────────────────── */
  .cover{position:relative;overflow:hidden;min-height:100vh;display:flex;flex-direction:column;
         justify-content:center;gap:2.2rem;max-width:46rem;margin:0 auto;padding:4.5rem 1.75rem;
         color:#fff}
  .cover::before{content:"";position:absolute;inset:0;pointer-events:none;
         background:radial-gradient(52rem 36rem at 88% -12%,color-mix(in srgb,var(--accent) 34%,transparent),transparent 62%),
                    radial-gradient(40rem 30rem at -18% 108%,color-mix(in srgb,var(--accent) 16%,transparent),transparent 60%)}
  .cover>*{position:relative}
  .cover-eyebrow{display:flex;align-items:center;gap:.6rem;font-size:.72rem;font-weight:700;
         letter-spacing:.16em;text-transform:uppercase;color:var(--night-muted)}
  .cover-eyebrow b{color:var(--accent)}
  .cover-eyebrow::after{content:"";flex:1;height:1px;background:var(--night-line)}
  .arch-name{font-size:clamp(2.9rem,9vw,4.6rem);line-height:.98;letter-spacing:-.025em;font-weight:800;
         text-transform:uppercase;color:var(--accent)}
  .arch-tagline{font-size:1.2rem;color:#fff;max-width:30rem;line-height:1.45}
  .panels{display:grid;gap:1rem}
  .panel{border:1px solid var(--night-line);border-radius:14px;padding:1.15rem 1.25rem;
         background:rgba(255,255,255,.03)}
  .panel h3{font-size:.68rem;font-weight:700;letter-spacing:.14em;text-transform:uppercase;
         color:var(--night-muted);margin-bottom:.9rem}
  .fp-row{display:grid;grid-template-columns:1fr 5.5rem 1fr;align-items:center;gap:.8rem;
         padding:.42rem 0;font-size:.74rem;color:var(--night-muted);line-height:1.3}
  .fp-label{text-align:right}
  .fp-right{text-align:left}
  .fp-track{position:relative;height:2px;background:var(--night-line);border-radius:2px}
  .fp-dot{position:absolute;top:50%;width:11px;height:11px;border-radius:99px;background:var(--accent);
         transform:translate(-50%,-50%);box-shadow:0 0 0 3px color-mix(in srgb,var(--accent) 28%,transparent)}
  .lean-bar{display:flex;height:10px;border-radius:99px;overflow:hidden;gap:2px}
  .lean-labels{display:flex;justify-content:space-between;margin-top:.65rem;font-size:.78rem;
         color:var(--night-muted)}
  .lean-labels i{display:inline-block;width:8px;height:8px;border-radius:99px;margin-right:.4rem}
  .score{color:#fff;font-size:1rem}
  .score strong{color:var(--accent)}
  .cover-quote{border-left:3px solid var(--accent);padding:.35rem 0 .35rem 1.1rem;font-size:1.25rem;
         line-height:1.5;color:#fff;font-style:italic}
  .cover-quote cite{display:block;margin-top:.55rem;font-size:.74rem;font-style:normal;
         letter-spacing:.08em;text-transform:uppercase;color:var(--night-muted)}
  .cover-foot{display:flex;justify-content:space-between;font-size:.74rem;color:var(--night-muted);
         letter-spacing:.04em}

  /* ── Page 2 · the work sample ─────────────────────────────────────────── */
  .sheet-wrap{padding:0 1rem 3.5rem;max-width:48rem;margin:0 auto}
  .sheet{background:#fff;border-radius:18px;padding:3.25rem 2.75rem 2.75rem;
         box-shadow:0 30px 80px rgba(0,0,0,.5)}
  .sheet header{border-top:4px solid var(--accent);padding-top:1.4rem;margin-bottom:2.2rem}
  .brand{font-size:.7rem;font-weight:700;letter-spacing:.14em;text-transform:uppercase;
         color:var(--accent);margin-bottom:1.1rem}
  h1{font-size:1.95rem;line-height:1.15;letter-spacing:-.02em;color:var(--ink);margin-bottom:.35rem;
     font-weight:700}
  .sub{color:var(--muted);font-size:.95rem}
  .tag{display:inline-block;margin-left:.4rem;padding:.1rem .45rem;border:1px solid var(--line);
       border-radius:99px;font-size:.7rem;text-transform:uppercase;letter-spacing:.06em;color:var(--muted)}
  section{margin:2.2rem 0}
  h2{font-size:.72rem;font-weight:700;letter-spacing:.12em;text-transform:uppercase;color:var(--muted);
     margin-bottom:.85rem;padding-bottom:.5rem;border-bottom:1px solid var(--line)}
  .sheet p{margin-bottom:.9rem}
  table{width:100%;border-collapse:collapse;font-variant-numeric:tabular-nums}
  th[scope=row]{text-align:left;font-weight:600;color:var(--ink);padding:.7rem 0;
     border-bottom:1px solid var(--line);width:52%}
  th small{display:block;font-weight:400;color:var(--muted);font-size:.78rem;margin-top:.1rem}
  td{padding:.7rem .5rem;border-bottom:1px solid var(--line);vertical-align:top}
  .num{text-align:right;font-weight:600;color:var(--ink);white-space:nowrap}
  .muted{color:var(--muted);font-weight:400}
  .ok{color:#047857;font-size:.8rem;font-weight:600}
  .off{color:var(--muted);font-size:.8rem}
  .skip{color:var(--muted);font-weight:400}
  thead th{font-size:.7rem;letter-spacing:.08em;text-transform:uppercase;color:var(--muted);
     font-weight:600;text-align:right;padding:0 .5rem .5rem;border-bottom:1px solid var(--line)}
  thead th:first-child{text-align:left}
  .sheet blockquote{padding:1rem 1.2rem;background:#f9fafb;border-left:3px solid var(--accent);
     border-radius:0 6px 6px 0;color:var(--ink);font-size:1.05rem}
  ul{padding-left:1.15rem}
  li{margin-bottom:.35rem}
  .style-name{font-weight:700;color:var(--ink)}
  .sheet footer{margin-top:3rem;padding-top:1.2rem;border-top:1px solid var(--line);font-size:.82rem;
     color:var(--muted)}

  @media (max-width:640px){
    .sheet{padding:2.25rem 1.4rem 2rem}
    .fp-row{grid-template-columns:1fr 3.5rem 1fr;gap:.5rem;font-size:.68rem}
  }
  @media print{
    body{background:#fff}
    .cover{min-height:auto;padding:3rem 2rem;border-radius:0;
           -webkit-print-color-adjust:exact;print-color-adjust:exact;
           background:var(--night);break-after:page}
    .sheet-wrap{padding:0}
    .sheet{box-shadow:none;border-radius:0;padding:2.5rem 0 0}
  }
</style></head><body>

<div class="cover">
  <p class="cover-eyebrow"><b>Extern Labs</b> ${escapeHtml(artifact.trackName)}</p>
  <div>
    <div class="arch-name">${escapeHtml(artifact.archetypeName)}</div>
    <p class="arch-tagline">${escapeHtml(artifact.archetypeTagline)}</p>
  </div>
  <div class="panels">
    ${fingerprintHtml(artifact.fingerprint)}
    ${leanHtml(artifact.lean)}
    ${scorePanel}
  </div>
  ${quote}
  <p class="cover-foot"><span>${escapeHtml(completedOn)}</span><span>extern.com</span></p>
</div>

<div class="sheet-wrap"><div class="sheet">
<header>
  <p class="brand">Extern Labs · ${escapeHtml(artifact.trackName)}</p>
  <h1>Operating analysis</h1>
  <p class="sub">${escapeHtml(artifact.subjectName)}<span class="tag">practice case</span></p>
</header>

<section>
  <h2>What this covers</h2>
  <p>Reading a three-year operating table and computing derived rates (${escapeHtml(coverage)}) to find whether quality held up as volume grew.</p>
</section>

<section>
  <h2>The rates I worked out</h2>
  <table>
    <thead><tr><th>Rate</th><th>Mine</th><th>Actual</th><th></th></tr></thead>
    <tbody>${rateRows}</tbody>
  </table>
</section>

${readBlock}

<section>
  <h2>What the numbers showed</h2>
  <p>${escapeHtml(artifact.finding)}</p>
</section>

<section>
  <h2>How I work</h2>
  <p><span class="style-name">${escapeHtml(artifact.archetypeName)}</span> · ${escapeHtml(artifact.archetypeTagline)}</p>
  <p>${escapeHtml(artifact.archetypeBody)}</p>
</section>

${interestsBlock}

<footer>Completed ${escapeHtml(completedOn)} as a short Extern Labs exercise. Extern runs real projects with real companies; this is a practice case built on the same mechanics.</footer>
</div></div>
</body></html>`;
}

/** A filename that reads like something worth keeping, not an export dump. */
export function artifactFileName(artifact: PreviewArtifact): string {
  const archetype = artifact.archetypeName
    .toLowerCase()
    .replace(/[^a-z0-9]+/g, '-')
    .replace(/^-|-$/g, '');
  return `extern-labs-${archetype || 'readout'}.html`;
}
